import { useEffect } from 'react';
import { View, Pressable, Text, Platform, StatusBar } from 'react-native';
import { mazeStrikerViewStyles as styles } from './MazeStrikerView.styles';
import { MazeStrikerWebView } from './MazeStrikerWebView';
import { MazeStrikerOverlays } from './MazeStrikerOverlays';
import { WarmupToast } from '../WarmupToast/WarmupToast';
import { useMazeStrikerGame } from '../../../src/quiz/hooks/useMazeStrikerGame';
import { MAZE_STRIKER_TEST_IDS } from '../../../src/quiz/constants/mazeStriker.constants';

interface MazeStrikerViewProps {
  onMoreGames: () => void;
}

/**
 * Fullscreen host for the MazeStriker runtime (the hub's first 3D/WebGL game). The runtime is a full-
 * viewport three.js scene + HUD that sizes itself to whatever box it gets; the host only provides the
 * back control and the loading/error overlays.
 */
export function MazeStrikerView({ onMoreGames }: MazeStrikerViewProps) {
  const game = useMazeStrikerGame();

  // Hide status bar on native while the game is open.
  useEffect(() => {
    if (Platform.OS !== 'web') StatusBar.setHidden(true, 'fade');
    return () => { if (Platform.OS !== 'web') StatusBar.setHidden(false, 'fade'); };
  }, []);

  // Back leaves immediately, closing the funnel first (the runtime keeps its own restart on the over screen).
  const handleBack = () => {
    game.confirmExit();
    onMoreGames();
  };

  return (
    <View style={styles.fullscreenContainer} testID={MAZE_STRIKER_TEST_IDS.view}>
      {/* Back arrow — always top-left, on top of everything */}
      <Pressable
        testID={MAZE_STRIKER_TEST_IDS.back}
        style={({ pressed }) => [styles.backButton, pressed && styles.pressedDim]}
        onPress={handleBack}
      >
        <Text style={styles.backArrow}>←</Text>
      </Pressable>

      <View style={styles.canvasWrap}>
        <MazeStrikerWebView
          ref={(r) => game.bindWebView(r)}
          html={game.html}
          onMessage={game.onMessage}
        />
      </View>

      <MazeStrikerOverlays status={game.status} onRetry={game.retry} />
      <WarmupToast message={game.toast} />
    </View>
  );
}
